import type { ProductStatus } from "@adpilot/shared";
import { useQuery } from "@tanstack/react-query";
import { resolveProductListings } from "@/lib/product-listing-api";

interface ProductIdentity {
  /** The listing key: a seller's SKU (smart creation and template products carry it as `sku`). */
  sku: string;
  asin: string;
}

/** Advertising status per listing key, read from the catalog resolution of the given products. */
export function useProductPreviewStatuses(shopId: string, products: readonly ProductIdentity[]) {
  const identities = products.map(({ sku, asin }) => ({ sku, asin }));
  const query = useQuery({
    queryKey: ["product-listings", "preview-status", shopId, identities],
    queryFn: () =>
      resolveProductListings(
        shopId,
        identities.map(({ sku }) => sku),
      ),
    enabled: products.length > 0,
    staleTime: 30_000,
    retry: false,
  });
  const statuses = new Map<string, ProductStatus>();
  for (const result of query.data?.results ?? []) {
    if (
      (result.type === "resolved" || result.type === "inactive") &&
      identities.some(
        (product) =>
          product.sku === result.listing.listingKey && product.asin === result.listing.asin,
      )
    ) {
      statuses.set(result.listing.listingKey, result.listing.productStatus);
    }
  }
  return statuses;
}
